import { useState } from 'react';

import {
  CsvDataSource,
  DatasourceInstructionMode,
  DealItemAnchorKey,
  AddCsvDataSourceToDealDocument,
  GetSignedUrlDocument,
  MarkDataSourceAsImportedDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { DomainError } from '@/types/data-source';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';

import { dealAtom } from '../../states/deal.state';

const upload = async (
  file: File,
  signedUrl: string
): Promise<{ ok: true } | { ok: false; message: string }> => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Cache-Control': 'no-cache',
    },
    body: file,
  });
  if (!response.ok) return { ok: false, message: 'Failed to upload file' };
  return { ok: true };
};

const useWatchDataSourceCsvProcessed = (): CsvDataSource[] => {
  const deal = useAtomValue(dealAtom);
  return (
    deal?.dataSources.filter(
      (ds: { __typename?: string }): ds is CsvDataSource =>
        ds.__typename === 'CSVDataSource'
    ) ?? []
  );
};

export const useUploadCsv = (
  dealId: string,
  instructionMode?: DatasourceInstructionMode,
  anchorKey?: DealItemAnchorKey | null
) => {
  const [dataSourceId, setDataSourceId] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<DomainError | null>(null);

  const dataSources = useWatchDataSourceCsvProcessed();

  const [addCsvDataSourceToDeal] = useMutation(AddCsvDataSourceToDealDocument);
  const [getSignedUrlAsync, { data, loading, error }] = useLazyQuery(
    GetSignedUrlDocument,
    { fetchPolicy: 'no-cache' }
  );

  const [markDataSourceAsImported] = useMutation(
    MarkDataSourceAsImportedDocument
  );
  const mutation = async (
    file: File,
    eMerchCustomFieldsHeaders?: string[]
  ): Promise<
    { success: true; id: string } | { success: false; domainError: DomainError }
  > => {
    setDomainError(null);
    trackEvent('IMPORT_STARTED', { dealId, source: 'csv' });

    const trackFailure = (errorCode: string, step: string) => {
      trackEvent('IMPORT_FAILED', {
        dealId,
        source: 'csv',
        errorCode,
        step,
      });
    };

    let currentDataSourceId: string;

    // 1. Créer la data source (peut retourner dealNotFound ou canNotAddDataSource)
    const { data: addCsvDataSourceToDealData } = await addCsvDataSourceToDeal({
      variables: {
        dealId,
        instructionMode,
        anchorKey,
        eMerchCustomFieldsHeaders,
      },
    });

    const result = addCsvDataSourceToDealData?.addCsvDataSourceToDeal;

    if (result?.__typename === 'MutationSuccess') {
      currentDataSourceId = result.id;
      setDataSourceId(currentDataSourceId);
    } else if (result?.__typename === 'DomainException') {
      const err = {
        code: result.code,
        message: `${result.code}`,
      };
      setDomainError(err);
      trackFailure(err.code, 'addCsvDataSource');
      return { success: false, domainError: err };
    } else {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'Unknown mutation result',
      };
      setDomainError(err);
      trackFailure(err.code, 'addCsvDataSource');
      return { success: false, domainError: err };
    }

    const { data: signedUrlData } = await getSignedUrlAsync({
      variables: { dealId },
    });

    const csvDataSourceItem = signedUrlData?.deal?.dataSources.find(
      (dataSource: {
        __typename?: string;
        id?: string;
      }): dataSource is CsvDataSource =>
        dataSource?.__typename === 'CSVDataSource' &&
        'id' in dataSource &&
        dataSource.id === currentDataSourceId
    );

    if (!csvDataSourceItem) {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'CSV data source not found after creation',
      };
      setDomainError(err);
      trackFailure(err.code, 'getSignedUrl');
      return { success: false, domainError: err };
    }

    if (!csvDataSourceItem.signedUrl) {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'No signed URL found',
      };
      setDomainError(err);
      trackFailure(err.code, 'getSignedUrl');
      return { success: false, domainError: err };
    }

    const uploadResult = await upload(file, csvDataSourceItem.signedUrl);
    if (!uploadResult.ok) {
      const err = {
        code: 'IMPORT_FAILED',
        message: uploadResult.message,
      };
      setDomainError(err);
      trackFailure(err.code, 'upload');
      return { success: false, domainError: err };
    }

    const { data: markData } = await markDataSourceAsImported({
      variables: {
        dealId,
        dataSourceId: currentDataSourceId,
      },
    });

    const markResult = markData?.markDataSourceAsImported;
    if (markResult?.__typename === 'DomainException') {
      const err = {
        code: markResult.code,
        message: `${markResult.code}`,
      };
      setDomainError(err);
      trackFailure(err.code, 'markAsImported');
      return { success: false, domainError: err };
    }

    trackEvent('IMPORT_COMPLETED', {
      dealId,
      source: 'csv',
    });
    return { success: true, id: currentDataSourceId };
  };

  const dataSourceStatus = dataSources.find(
    (ds) => ds.id === dataSourceId
  )?.status;

  return {
    data,
    loading,
    errors: error,
    addCsvDataSourceToDeal: mutation,
    dataSourceStatus,
    domainError,
    isDomainError: Boolean(domainError),
  };
};
